/**
 * Host-side routine-candidate extraction (ADR-0041 D4, ENG-7420).
 *
 * Rides the memory-extraction pass (memory-extractor.ts): for each completed
 * end-user conversation the manager reconstructs, this module additionally
 * looks for a durable RECURRING ask ("every morning...", the third "what came
 * in overnight?" this week) and POSTs a low-sensitivity routine candidate to
 * POST /host/routines/candidates, where the API-side accumulator
 * (routine_observations) does the cross-conversation evidence counting.
 *
 * SECURITY / DATA-MINIMISATION:
 *  - Same boundary as memory extraction: the transcript never leaves the
 *    host. Only {description, cadence_guess, evidence_count, confidence}
 *    crosses the wire, with the description scrubbed for secret shapes.
 *  - UNTRUSTED-CONTENT POSTURE (the ADR's injection rule): only USER-authored
 *    asks may drive a candidate. The extraction input is built exclusively
 *    from role==='user' turns of the reconstructed conversation - assistant
 *    turns (whose text embeds tool-retrieved, possibly poisoned content)
 *    never reach the routine extractor at all. A hostile web page telling the
 *    agent to "set up a daily task" is structurally incapable of becoming
 *    evidence, rather than relying on the model to ignore it.
 *
 * Same fire-and-forget contract as its siblings: best-effort, all faults
 *    swallowed, must never slow the manager poll cycle or interfere with
 *    memory extraction / conversation finalisation. A conversation gets one
 *    emission opportunity (memory extraction finalises it server-side); a
 *    missed cycle just loses one observation, which the accumulator's
 *    repetition threshold tolerates by design.
 */

import type { ConversationEvalBackend, ConversationEvaluatorApi, Turn } from './conversation-evaluator.js';
import { scrubSensitive } from './memory-extractor.js';

/** Cadence guess vocabulary. Free-text cadences collapse to 'unknown'. */
const VALID_CADENCE_GUESSES = new Set(['daily', 'weekdays', 'weekly', 'unknown']);

// Bound the extraction input: recurring asks are short; a runaway transcript
// must not balloon the prompt.
const MAX_ASK_CHARS = 400;
const MAX_INPUT_CHARS = 6_000;

export interface RoutineCandidate {
  /**
   * Short canonical description of the recurring ask. Bounded at parse
   * (<=500); the wire payload is scrubbed then truncated to <=200.
   */
  description: string;
  cadence_guess: 'daily' | 'weekdays' | 'weekly' | 'unknown';
  /** How many user messages in THIS conversation evidence the ask (>=1). */
  evidence_count: number;
  confidence: number;
}

/**
 * Build the extraction input from the reconstructed conversation: USER turns
 * only (the injection boundary - see module doc). Exported for tests.
 */
export function buildUserAsksInput(turns: Turn[]): string {
  const lines: string[] = [];
  let total = 0;
  for (const turn of turns) {
    if (turn.role !== 'user') continue;
    const body =
      turn.text.length > MAX_ASK_CHARS ? `${turn.text.slice(0, MAX_ASK_CHARS)}…` : turn.text;
    const line = `User: ${body}`;
    if (total + line.length > MAX_INPUT_CHARS) break;
    lines.push(line);
    total += line.length;
  }
  return lines.join('\n');
}

/** Build the routine-candidate prompt over the user-authored asks only. */
export function buildRoutinePrompt(channel: string, userAsks: string): string {
  return `You are looking for a DURABLE RECURRING ASK in the user's messages from one completed ${channel} conversation with an AI agent. A routine candidate is something the user asks for repeatedly or explicitly wants on a schedule ("every morning", "each Friday", "again" for the third time) - not a one-off task.

User messages (agent replies deliberately omitted):
"""
${userAsks}
"""

Extract 0-2 routine candidates. For each:
- description: the recurring ask, self-contained and generic (max 200 chars). No secrets, no personal data beyond what is needed to name the task.
- cadence_guess: "daily", "weekdays", "weekly", or "unknown" if the messages imply repetition without a clear cadence
- evidence_count: how many of the user messages above support this ask (integer >= 1)
- confidence: 0.0-1.0 - how sure you are this is a genuine recurring responsibility

Most conversations contain NO routine candidate - return an empty array unless the recurrence signal is clear. Do NOT invent candidates.

Respond with ONLY a JSON array, no other text:
[{"description":"...","cadence_guess":"daily","evidence_count":2,"confidence":0.7}]`;
}

/**
 * Parse the model output. Same contract as memory-extractor's parseCandidates:
 * `null` = no parseable JSON array (transient - caller just drops this cycle),
 * array (possibly empty) = a real answer.
 */
export function parseRoutineCandidates(raw: string): RoutineCandidate[] | null {
  const match = raw.match(/\[[\s\S]*\]/);
  if (!match) return null;
  let arr: unknown;
  try {
    arr = JSON.parse(match[0]);
  } catch {
    return null;
  }
  if (!Array.isArray(arr)) return null;
  const out: RoutineCandidate[] = [];
  for (const entry of arr) {
    if (!entry || typeof entry !== 'object') continue;
    const c = entry as Partial<RoutineCandidate>;
    const description = typeof c.description === 'string' ? c.description.trim() : '';
    if (!description) continue;
    const confidence =
      typeof c.confidence === 'number' && Number.isFinite(c.confidence)
        ? Math.max(0, Math.min(1, c.confidence))
        : 0;
    if (confidence === 0) continue;
    const cadence =
      typeof c.cadence_guess === 'string' && VALID_CADENCE_GUESSES.has(c.cadence_guess)
        ? (c.cadence_guess as RoutineCandidate['cadence_guess'])
        : 'unknown';
    const evidenceRaw =
      typeof c.evidence_count === 'number' && Number.isFinite(c.evidence_count)
        ? Math.floor(c.evidence_count)
        : 1;
    out.push({
      // Bounded parse only - the FINAL 200-char truncation happens after
      // scrubbing (maybeEmitRoutineCandidates), because slicing first can
      // split a secret across the cut so the scrub regex no longer matches
      // and a fragment leaks onto the wire.
      description: description.slice(0, 500),
      cadence_guess: cadence,
      evidence_count: Math.max(1, Math.min(50, evidenceRaw)),
      confidence,
    });
  }
  return out;
}

/**
 * Extract and emit routine candidates for one reconstructed conversation.
 * Called from the memory-extraction loop; never throws.
 */
export async function maybeEmitRoutineCandidates(args: {
  api: ConversationEvaluatorApi;
  backend: ConversationEvalBackend;
  codeName: string;
  agentId: string;
  conversationId: string;
  channel: string;
  turns: Turn[];
  log: (msg: string) => void;
  onModelCallError?: (err: unknown) => void;
}): Promise<void> {
  const { api, backend, codeName, agentId, conversationId, channel, turns, log } = args;
  try {
    const userAsks = buildUserAsksInput(turns);
    if (!userAsks.trim()) return;

    let candidates: RoutineCandidate[] | null;
    try {
      const out = await backend.run(buildRoutinePrompt(channel, userAsks));
      candidates = parseRoutineCandidates(out);
    } catch (err) {
      log(`[routine-extract] ${codeName}: extraction failed: ${(err as Error).message}`);
      args.onModelCallError?.(err);
      return;
    }
    // Unparseable or empty: nothing to emit. Unlike memory extraction there is
    // no retry loop to protect - the accumulator tolerates a lost observation.
    if (!candidates || candidates.length === 0) return;

    // Scrub FIRST, truncate second: truncating a raw description can split a
    // secret shape across the cut and defeat the scrub patterns.
    const scrubbed = candidates.map((c) => ({
      ...c,
      description: scrubSensitive(c.description).slice(0, 200),
    }));

    try {
      const res = await api.post<{ ok?: boolean; discarded?: boolean; accepted?: number }>(
        '/host/routines/candidates',
        {
          agent_id: agentId,
          conversation_id: conversationId,
          candidates: scrubbed,
        },
      );
      log(
        `[routine-extract] ${codeName}: ${conversationId.slice(0, 8)} → ${scrubbed.length} candidate(s)${res?.discarded ? ' (discarded: feature off)' : ''}`,
      );
    } catch (err) {
      log(`[routine-extract] ${codeName}: report failed: ${(err as Error).message}`);
    }
  } catch (err) {
    // Belt-and-braces: this helper must never disturb the memory pass.
    log(`[routine-extract] ${codeName}: unexpected error: ${(err as Error).message}`);
  }
}
